import { NORA_USERINFO_SCHEMA } from '@bldsoft/stpanalytix'
import { Logger } from '~/shared/logger'

/**
 * Debug logger for raw Analytix adapter traffic.
 * Disabled by default to avoid noisy production logs.
 */
export const logger = new Logger('tria/analytix')

/**
 * Mapping from adapter methods to wire event names expected by backend.
 */
export const ANALYTIX_EVENT_NAMES = {
  sendSessionStartEvent: 'session_start',
  sendSessionEndEvent: 'session_end',
  sendFavoriteAddEvent: 'favorite_add',
  sendFavoriteRemoveEvent: 'favorite_remove',
  sendPlayerOpenEvent: 'player_open',
  sendPlayStartEvent: 'play_start',
  sendPlayStopEvent: 'play_stop',
  sendSearchEvent: 'search',
  sendStreamInfoEvent: 'stream_info',
  sendCDNInfoEvent: 'cdn_info',
  sendRemoteControlActionEvent: 'remote_control_action',
  sendUIActionEvent: 'ui_action',
} as const

/**
 * Mapping of global/user fields to provider API field names.
 */
export const ANALYTIX_GENERAL_PARAM_NAMES = NORA_USERINFO_SCHEMA.reduce(
  (acc, curr) => ((acc[curr.name] = curr.apiField), acc),
  {} as Record<string, string>
)

/**
 * Mapping of event payload keys to provider event parameter slots.
 */
export const ANALYTIX_EVENT_PARAM_NAMES = {
  session_start: {
    userAgent: 'event_parameter1',
  },
  session_end: {},
  favorite_add: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    contentName: 'event_parameter3',
  },
  favorite_remove: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    contentName: 'event_parameter3',
  },
  player_open: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    contentUrl: 'event_parameter3',
    contentName: 'event_parameter4',
    programName: 'event_parameter5',
    contentOpenFrom: 'event_parameter6',
    isContentPaid: 'event_parameter18',
  },
  play_start: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    contentUrl: 'event_parameter3',
    contentName: 'event_parameter4',
    programName: 'event_parameter5',
    startSecond: 'event_parameter6',
    contentLength: 'event_parameter7',
    contentPlayReasons: 'event_parameter8',
    httpErrorCode: 'event_parameter10',
    contentErrorCode: 'event_parameter11',
    audioTrackLanguage: 'event_parameter12',
    subtitlesLanguage: 'event_parameter13',
    videoQuality: 'event_parameter17',
    isContentPaid: 'event_parameter18',
    playbackSessionId: 'event_parameter20',
  },
  play_stop: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    contentUrl: 'event_parameter3',
    contentName: 'event_parameter4',
    programName: 'event_parameter5',
    stopSecond: 'event_parameter6',
    contentLength: 'event_parameter7',
    contentStopReasons: 'event_parameter8',
    httpErrorCode: 'event_parameter10',
    contentErrorCode: 'event_parameter11',
    audioTrackLanguage: 'event_parameter12',
    subtitlesLanguage: 'event_parameter13',
    totalWatchedTimeFromLatestPlayStart: 'event_parameter14',
    categoryName: 'event_parameter15',
    videoQuality: 'event_parameter17',
    isContentPaid: 'event_parameter18',
    playbackSessionId: 'event_parameter20',
  },
  search: {
    inputString: 'event_parameter1',
    searchContentType: 'event_parameter2',
  },
  stream_info: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    contentUrl: 'event_parameter3',
    contentName: 'event_parameter4',
    bitrate: 'event_parameter5',
    bufferState: 'event_parameter6',
    cdnTraffic: 'event_parameter7',
    contentDrm: 'event_parameter8',
    videoQuality: 'event_parameter17',
    isContentPaid: 'event_parameter18',
    playbackSessionId: 'event_parameter20',
  },
  cdn_info: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    manifest: 'event_parameter3',
    cdnLatency: 'event_parameter4',
    cdnUrl: 'event_parameter5',
    playbackSessionId: 'event_parameter20',
  },
  remote_control_action: {
    keyCode: 'event_parameter1',
    keyCodeName: 'event_parameter2',
    buttonState: 'event_parameter3',
    testNameAndGroup: 'event_parameter7',
    timestampMs: 'event_parameter8',
  },
  ui_action: {
    contentId: 'event_parameter1',
    contentType: 'event_parameter2',
    contentName: 'event_parameter4',
    programName: 'event_parameter5',
    route: 'event_parameter6',
    serviceName: 'event_parameter7',
    accessInfo: 'event_parameter8',
    featureIdentifier: 'event_parameter10',
    uiElementType: 'event_parameter11',
    uiElementName: 'event_parameter12',
    actionName: 'event_parameter13',
    eventParameter14: 'event_parameter14',
    eventParameter15: 'event_parameter15',
    eventParameter16: 'event_parameter16',
    eventParameter17: 'event_parameter17',
    eventParameter18: 'event_parameter18',
    playbackSessionId: 'event_parameter20',
  },
} as const

type AnalytixDebugParams = Record<string, unknown>

// Shared utility log function for any Bus events.
export const debugAnalytixEvent = ({
  eventName,
  params = {},
  general = {},
  emoji,
}: {
  eventName: string
  params?: AnalytixDebugParams
  general?: AnalytixDebugParams
  emoji?: string
}) => {
  const title = emoji ? `${emoji} ${eventName}` : eventName
  const glogger = logger.debugGroup(title)
  const table: Record<string, { parameter: string; value: unknown }> = {
    eventName: { parameter: 'event_name', value: eventName },
  }

  // add user info general parameters
  for (const [key, value] of Object.entries(general)) {
    table[key] = {
      parameter: ANALYTIX_GENERAL_PARAM_NAMES[key] ?? key,
      value: value,
    }
  }

  // sort and add event parameters
  const parameters = ANALYTIX_EVENT_PARAM_NAMES[eventName] ?? {}
  const compare = new Intl.Collator(undefined, { numeric: true }).compare
  Object.entries(params)
    .filter(([key]) => !key.startsWith('('))
    .map(([key, value]) => ({
      key,
      value,
      parameter: parameters[key] ?? key,
    }))
    .sort((a, b) => compare(a.parameter, b.parameter))
    .forEach(({ key, ...row }) => (table[key] = row))

  glogger?.table(table)
  glogger?.groupEnd()
}
